import {
  DEFAULT_NOTIFY_PREFS,
  hydrateMoneyConfig,
  emptyDraftPoll,
  toggleApproval,
  pruneOption,
  type SeasonBundle,
  type TrashPost,
  type DraftPoll,
  type NotifyPrefs,
  type WeekLineups,
  type TipSheet,
  type TipSheetStatus,
} from '@ff/core';
import { getFirebaseAuth } from './firebase.ts';

/**
 * When VITE_USE_MOCK is set, the app runs entirely off a bundled real-data
 * snapshot with no backend — used for local UI work and screenshots. Otherwise
 * every call carries the Firebase ID token and hits the API.
 */
const USE_MOCK = import.meta.env.VITE_USE_MOCK === '1';

export interface StoredSeason {
  season: number;
  bundle: SeasonBundle;
  syncedAt: string;
  latestCompletedWeek: number;
  /** Set by the API on the current season during an NFL game window, so the
   * client keeps polling through kickoff before any score exists. */
  inGameWindow?: boolean;
}

async function authedFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const user = getFirebaseAuth().currentUser;
  const token = user ? await user.getIdToken() : null;
  return fetch(path, {
    ...init,
    headers: {
      ...init.headers,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
}

const jsonHeaders = { 'content-type': 'application/json' };

async function json<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(res.status, (body as { error?: string }).error ?? res.statusText);
  }
  return res.json() as Promise<T>;
}

export class ApiError extends Error {
  constructor(readonly status: number, message: string) {
    super(message);
  }
}

export async function fetchSeasons(): Promise<number[]> {
  if (USE_MOCK) return (await import('../mock/season2025.json')).default ? [2025] : [];
  const { seasons } = await json<{ seasons: number[] }>(await authedFetch('/api/seasons'));
  return seasons;
}

export async function fetchSeason(season: number): Promise<StoredSeason> {
  if (USE_MOCK) {
    const mock = (await import('../mock/season2025.json')).default as unknown as StoredSeason;
    // The API repairs every stored config on the way out; the snapshot has to
    // be read the same way or the preview drifts from what members see.
    const config = hydrateMoneyConfig(mock.bundle.money.config);
    return { ...mock, bundle: { ...mock.bundle, money: { ...mock.bundle.money, config } } };
  }
  return json<StoredSeason>(await authedFetch(`/api/seasons/${season}`));
}

export interface Member {
  email: string;
  name: string;
  isCommissioner: boolean;
}

export async function fetchMe(): Promise<Member> {
  // In preview there is no backend; pretend to be a commissioner so the UI can
  // be exercised locally.
  if (USE_MOCK) return { email: 'preview@local', name: 'Preview', isCommissioner: true };
  return json<Member>(await authedFetch('/api/me'));
}

/**
 * The whole league roster, so votes can be shown as "still waiting on X and Y"
 * instead of only counting the people who already answered.
 */
export async function fetchLeagueMembers(): Promise<{ email: string; name: string }[]> {
  if (USE_MOCK) {
    // Mirrors the mock ballots below, plus two members who haven't voted, so
    // the preview shows both halves of the list.
    return [
      { email: 'preview@local', name: 'Preview' },
      { email: 'ryan@local', name: 'Dana Whitfield' },
      { email: 'tj@local', name: 'Sam Okafor' },
      { email: 'kyle@local', name: 'Ravi Menon' },
      { email: 'marcus@local', name: 'Alex Petrov' },
    ];
  }
  const { members } = await json<{ members: { email: string; name: string }[] }>(
    await authedFetch('/api/members'),
  );
  return members;
}

export interface Recap {
  season: number;
  week: number;
  text: string;
  generatedAt: string;
  model: string;
}

export async function fetchRecap(season: number, week: number): Promise<Recap | null> {
  if (USE_MOCK) return null;
  const res = await authedFetch(`/api/seasons/${season}/recaps/${week}`);
  if (res.status === 404) return null;
  return json<Recap>(res);
}

export async function generateRecap(season: number, week: number): Promise<Recap> {
  return json<Recap>(
    await authedFetch(`/api/seasons/${season}/recaps/${week}`, { method: 'POST' }),
  );
}

// --- Weekly Tip Sheet ---

export type {
  TipSheet,
  TipSheetTeam,
  TipSheetPlayer,
  TipSheetSwap,
  TipSheetWaiver,
  TipVerdict,
  PowerRanking,
} from '@ff/core';

export interface TipSheetSummary {
  week: number;
  status: TipSheetStatus;
  generatedAt?: string;
  publishedAt?: string;
}

/** Which weeks have a sheet. Members only see published ones; the commissioner sees drafts too. */
export async function fetchTipSheetWeeks(season: number): Promise<TipSheetSummary[]> {
  if (USE_MOCK) return [{ week: 6, status: 'published', publishedAt: '2025-10-08T15:00:00.000Z' }];
  const { sheets } = await json<{ sheets: TipSheetSummary[] }>(
    await authedFetch(`/api/seasons/${season}/tip-sheets`),
  );
  return sheets;
}

/** Null when there is no sheet, or none the caller is allowed to see yet. */
export async function fetchTipSheet(season: number, week: number): Promise<TipSheet | null> {
  if (USE_MOCK) {
    const mock = (await import('../mock/tipSheet.json')).default as unknown as TipSheet;
    return { ...mock, season, week };
  }
  const res = await authedFetch(`/api/seasons/${season}/tip-sheets/${week}`);
  if (res.status === 404) return null;
  return json<TipSheet>(res);
}

/**
 * Kicks off generation in the background. Answers with the `generating`
 * document as written, so the page can show it and poll fetchTipSheet.
 */
export async function generateTipSheet(season: number, week: number): Promise<TipSheet> {
  if (USE_MOCK) {
    return {
      season, week, status: 'generating', teams: [], powerRankings: [],
      progress: { done: 0, total: 12 }, startedAt: new Date().toISOString(), model: 'mock',
    };
  }
  return json<TipSheet>(
    await authedFetch(`/api/seasons/${season}/tip-sheets/${week}`, { method: 'POST' }),
  );
}

/** Rewrite one team on the sheet; answers with the team stamped `regeneratingAt`. */
export async function regenerateTipSheetTeam(
  season: number,
  week: number,
  ownerId: string,
): Promise<TipSheet> {
  if (USE_MOCK) {
    const sheet = (await fetchTipSheet(season, week)) as TipSheet;
    return {
      ...sheet,
      teams: sheet.teams.map((t) =>
        t.ownerId === ownerId ? { ...t, regeneratingAt: new Date().toISOString() } : t,
      ),
    };
  }
  return json<TipSheet>(
    await authedFetch(
      `/api/seasons/${season}/tip-sheets/${week}/teams/${encodeURIComponent(ownerId)}`,
      { method: 'POST' },
    ),
  );
}

/** Rewrite the power rankings from the sheet's current teams. */
export async function regenerateTipSheetRankings(season: number, week: number): Promise<TipSheet> {
  if (USE_MOCK) {
    const sheet = (await fetchTipSheet(season, week)) as TipSheet;
    return { ...sheet, rankingsRegeneratingAt: new Date().toISOString() };
  }
  return json<TipSheet>(
    await authedFetch(`/api/seasons/${season}/tip-sheets/${week}/rankings`, { method: 'POST' }),
  );
}

/** Stop whatever is being written for this week; answers with the sheet as it now stands. */
export async function cancelTipSheet(season: number, week: number): Promise<TipSheet> {
  if (USE_MOCK) {
    const sheet = (await fetchTipSheet(season, week)) as TipSheet;
    return { ...sheet, status: 'draft', error: 'Cancelled' };
  }
  return json<TipSheet>(
    await authedFetch(`/api/seasons/${season}/tip-sheets/${week}/cancel`, { method: 'POST' }),
  );
}

export async function publishTipSheet(season: number, week: number): Promise<TipSheet> {
  if (USE_MOCK) return fetchTipSheet(season, week) as Promise<TipSheet>;
  return json<TipSheet>(
    await authedFetch(`/api/seasons/${season}/tip-sheets/${week}/publish`, { method: 'POST' }),
  );
}

/**
 * Pull this season fresh from ESPN. Commissioner only.
 *
 * The server syncs on its own during NFL game windows, which leaves everything
 * outside them — a draft on a Wednesday night, most obviously — waiting on the
 * cron. This is the manual pull for those.
 */
export async function syncSeasonNow(season: number): Promise<{ syncedAt: string }> {
  if (USE_MOCK) return { syncedAt: new Date().toISOString() };
  return json<{ syncedAt: string }>(
    await authedFetch(`/api/seasons/${season}/sync`, { method: 'POST' }),
  );
}

export async function createShareLink(season: number, week: number): Promise<{ url: string }> {
  return json<{ url: string }>(
    await authedFetch(`/api/seasons/${season}/weeks/${week}/share`, { method: 'POST' }),
  );
}

export interface Overrides {
  manualWinners?: Record<string, string>;
  placements?: Record<number, string>;
  paid?: Record<string, number>;
  boughtIn?: Record<string, boolean>;
  /** Award id -> owner ids the league has ruled out of that award. */
  ineligible?: Record<string, string[]>;
  /**
   * Change ONE award's ruling, merged server-side against whatever is stored.
   *
   * `ineligible` replaces the whole map, so two panels each posting their own
   * copy of it means the second silently undoes the first. Anything editing a
   * single award should send this instead.
   */
  ineligibleFor?: { awardId: string; ownerIds: string[] };
}

export async function fetchOverrides(season: number): Promise<Overrides> {
  if (USE_MOCK) return {};
  return json<Overrides>(await authedFetch(`/api/seasons/${season}/overrides`));
}

/**
 * Records manual state. `rebuilt` reports whether the server also managed to
 * rebuild the money board — the write lands either way, but a false means the
 * cached board is behind until the next sync.
 */
export async function saveOverrides(
  season: number,
  overrides: Overrides,
): Promise<{ rebuilt: boolean }> {
  if (USE_MOCK) return { rebuilt: true };
  const res = await authedFetch(`/api/seasons/${season}/overrides`, {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(overrides),
  });
  const body = await json<{ ok: boolean; rebuilt?: boolean }>(res);
  return { rebuilt: body.rebuilt !== false };
}

/** Avatars keyed by normalized manager name, for matching to board rows. */
export async function fetchAvatars(): Promise<Record<string, string>> {
  if (USE_MOCK) return {};
  const { avatars } = await json<{ avatars: Record<string, string> }>(
    await authedFetch('/api/avatars'),
  );
  return avatars;
}

export async function uploadAvatar(dataUrl: string): Promise<void> {
  if (USE_MOCK) return; // preview has no backend; the local preview state shows it
  const res = await authedFetch('/api/avatar', {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ dataUrl }),
  });
  await json(res);
}

/** Who started that week. Null when the sync hasn't stored that week yet. */
export interface StoredLineups extends WeekLineups {
  fetchedAt: string;
}

export async function fetchLineups(
  season: number,
  week: number,
): Promise<StoredLineups | null> {
  if (USE_MOCK) {
    // One week of real lineup shapes, re-keyed onto the preview season's owners
    // so every card opens. Design work on this component needs data in it.
    const mock = (await import('../mock/lineups.json')).default as unknown as StoredLineups;
    return { ...mock, season, week };
  }
  const res = await authedFetch(`/api/seasons/${season}/weeks/${week}/lineups`);
  if (res.status === 404) return null;
  return json<StoredLineups>(res);
}

// --- Unread trash talk ---

export interface UnreadBoard {
  week: number;
  count: number;
  /** The newest post this member has seen on that board; null if never opened. */
  readAt: string | null;
}

export async function fetchUnread(): Promise<{ season: number; boards: UnreadBoard[] }> {
  if (USE_MOCK) return { season: 2025, boards: [{ week: 6, count: 3, readAt: null }] };
  return json<{ season: number; boards: UnreadBoard[] }>(await authedFetch('/api/talk/unread'));
}

/** The member has seen a board up to the post created at `upTo`. */
export async function markTalkRead(season: number, week: number, upTo: string): Promise<void> {
  if (USE_MOCK) return;
  await json(
    await authedFetch('/api/talk/read', {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify({ season, week, upTo }),
    }),
  );
}

// --- Push notifications ---

export interface PushSettings {
  /** False when the server has no VAPID keys — the UI says so rather than failing. */
  enabled: boolean;
  prefs: NotifyPrefs;
  /** How many of this member's browsers are subscribed. */
  deviceCount: number;
}

export async function getPushPublicKey(): Promise<{ publicKey: string | null }> {
  if (USE_MOCK) return { publicKey: null };
  // Unauthenticated on purpose: the key is public, and the client needs it
  // before it has anything else.
  return json<{ publicKey: string | null }>(await fetch('/api/push/public-key'));
}

export async function putPushSubscription(subscription: PushSubscriptionJSON): Promise<void> {
  if (USE_MOCK) return;
  await json(
    await authedFetch('/api/push/subscription', {
      method: 'PUT',
      headers: jsonHeaders,
      body: JSON.stringify(subscription),
    }),
  );
}

export async function deletePushSubscription(endpoint: string): Promise<void> {
  if (USE_MOCK) return;
  await json(
    await authedFetch('/api/push/subscription', {
      method: 'DELETE',
      headers: jsonHeaders,
      body: JSON.stringify({ endpoint }),
    }),
  );
}

export async function fetchPushSettings(): Promise<PushSettings> {
  if (USE_MOCK) {
    return { enabled: false, prefs: DEFAULT_NOTIFY_PREFS, deviceCount: 0 };
  }
  return json<PushSettings>(await authedFetch('/api/push/settings'));
}

export async function savePushPrefs(prefs: NotifyPrefs): Promise<NotifyPrefs> {
  if (USE_MOCK) return prefs;
  const res = await json<{ prefs: NotifyPrefs }>(
    await authedFetch('/api/push/settings', {
      method: 'PUT',
      headers: jsonHeaders,
      body: JSON.stringify({ prefs }),
    }),
  );
  return res.prefs;
}

/** Returns how many devices it reached. */
export async function sendTestPush(): Promise<number> {
  if (USE_MOCK) return 0;
  const { sent } = await json<{ sent: number }>(
    await authedFetch('/api/push/test', { method: 'POST' }),
  );
  return sent;
}

// --- Trash talk ---

const talkBase = (season: number, week: number) =>
  `/api/seasons/${season}/weeks/${week}/talk`;

export async function fetchTalk(season: number, week: number): Promise<TrashPost[]> {
  if (USE_MOCK) return mockTalk.list(season, week);
  const { posts } = await json<{ posts: TrashPost[] }>(await authedFetch(talkBase(season, week)));
  return posts;
}

export async function postTalk(
  season: number,
  week: number,
  text: string,
): Promise<TrashPost[]> {
  if (USE_MOCK) return mockTalk.post(season, week, text);
  const { posts } = await json<{ posts: TrashPost[] }>(
    await authedFetch(talkBase(season, week), {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify({ text }),
    }),
  );
  return posts;
}

export async function deleteTalk(
  season: number,
  week: number,
  id: string,
): Promise<TrashPost[]> {
  if (USE_MOCK) return mockTalk.remove(season, week, id);
  const { posts } = await json<{ posts: TrashPost[] }>(
    await authedFetch(`${talkBase(season, week)}/${id}`, { method: 'DELETE' }),
  );
  return posts;
}

export async function reactTalk(
  season: number,
  week: number,
  id: string,
  emoji: string,
): Promise<TrashPost[]> {
  if (USE_MOCK) return mockTalk.react(season, week, id, emoji);
  const { posts } = await json<{ posts: TrashPost[] }>(
    await authedFetch(`${talkBase(season, week)}/${id}/react`, {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify({ emoji }),
    }),
  );
  return posts;
}

/**
 * A tiny in-memory board so the no-backend preview (VITE_USE_MOCK) is fully
 * interactive — post, react, delete all work against this store for the life of
 * the tab. Never reached with a real backend.
 */
const mockTalk = (() => {
  const boards = new Map<string, TrashPost[]>();
  const key = (s: number, w: number) => `${s}_${w}`;
  const me = 'preview@local';
  const r = (email: string, name: string) => ({ email, name });
  const gameSeed = (): TrashPost[] => [
    {
      id: 'seed-1',
      authorEmail: 'other@local',
      authorName: 'Dana Whitfield',
      text: 'bold of you to start that defense against the highest-scoring offense in the league',
      createdAt: new Date(Date.now() - 8 * 60_000).toISOString(),
      reactions: {
        '😂': [r('a@local', 'Sam Okafor'), r('b@local', 'Chris Bellamy'), r('c@local', 'Jordan Vance')],
        '🔥': [r('d@local', 'Casey Lindqvist')],
      },
    },
    {
      id: 'seed-2',
      authorEmail: me,
      authorName: 'Preview',
      text: 'my kicker outscored your first-round RB. sleep on that.',
      createdAt: new Date(Date.now() - 90_000).toISOString(),
      reactions: { '🧢': [r('e@local', 'Morgan Ellis'), r('f@local', 'Nico Ferreira')] },
    },
  ];
  const draftSeed = (): TrashPost[] => [
    {
      id: 'draft-1',
      authorEmail: 'other@local',
      authorName: 'Dana Whitfield',
      text: "keeping a WR2 at a 3rd-round price and calling it a steal. can't wait to draft next to you.",
      createdAt: new Date(Date.now() - 42 * 60_000).toISOString(),
      reactions: { '🤡': [r('a@local', 'Sam Okafor'), r('b@local', 'Chris Bellamy')], '😂': [r('c@local', 'Jordan Vance')] },
    },
    {
      id: 'draft-2',
      authorEmail: me,
      authorName: 'Preview',
      text: "whoever takes a QB in the first two rounds is paying for my beer at the draft party",
      createdAt: new Date(Date.now() - 5 * 60_000).toISOString(),
      reactions: { '🔥': [r('d@local', 'Casey Lindqvist'), r('e@local', 'Morgan Ellis')] },
    },
  ];
  const seed = (_s: number, w: number): TrashPost[] =>
    w === 0 ? draftSeed() : gameSeed();
  const get = (s: number, w: number) => {
    const k = key(s, w);
    if (!boards.has(k)) boards.set(k, seed(s, w));
    return boards.get(k)!;
  };
  return {
    async list(s: number, w: number) {
      return [...get(s, w)];
    },
    async post(s: number, w: number, text: string) {
      const board = get(s, w);
      board.push({
        id: `m-${board.length}-${text.length}`,
        authorEmail: me,
        authorName: 'Preview',
        text,
        createdAt: new Date().toISOString(),
        reactions: {},
      });
      return [...board];
    },
    async remove(s: number, w: number, id: string) {
      boards.set(key(s, w), get(s, w).filter((p) => p.id !== id));
      return [...boards.get(key(s, w))!];
    },
    async react(s: number, w: number, id: string, emoji: string) {
      const post = get(s, w).find((p) => p.id === id);
      if (post) {
        const who = post.reactions[emoji] ?? [];
        const next = who.some((x) => x.email === me)
          ? who.filter((x) => x.email !== me)
          : [...who, r(me, 'Preview')];
        if (next.length > 0) post.reactions = { ...post.reactions, [emoji]: next };
        else {
          const { [emoji]: _drop, ...rest } = post.reactions;
          post.reactions = rest;
        }
      }
      return [...get(s, w)];
    },
  };
})();

// --- Draft poll ---

const draftBase = (season: number) => `/api/seasons/${season}/draft-poll`;

export async function fetchDraftPoll(season: number): Promise<DraftPoll | null> {
  if (USE_MOCK) return mockDraft.get(season);
  const { poll } = await json<{ poll: DraftPoll | null }>(await authedFetch(draftBase(season)));
  return poll;
}

async function draftPost(path: string, body?: unknown): Promise<DraftPoll | null> {
  // Only declare a JSON body when there actually is one. Sending
  // `Content-Type: application/json` with an empty body makes Fastify's JSON
  // parser reject the request with 400 before the route runs — which broke the
  // no-body POSTs (start a vote, reopen a phase).
  const init: RequestInit =
    body === undefined
      ? { method: 'POST' }
      : { method: 'POST', headers: jsonHeaders, body: JSON.stringify(body) };
  const { poll } = await json<{ poll: DraftPoll | null }>(await authedFetch(path, init));
  return poll;
}

export async function createDraftPoll(season: number): Promise<DraftPoll | null> {
  if (USE_MOCK) return mockDraft.create(season);
  return draftPost(draftBase(season));
}

export async function resetDraftPoll(season: number): Promise<null> {
  if (!USE_MOCK) {
    await json(await authedFetch(draftBase(season), { method: 'DELETE' }));
  } else mockDraft.reset(season);
  return null;
}

export async function addDraftOption(season: number, label: string): Promise<DraftPoll | null> {
  if (USE_MOCK) return mockDraft.addOption(season, label);
  return draftPost(`${draftBase(season)}/options`, { label });
}

export async function removeDraftOption(
  season: number,
  optionId: string,
): Promise<DraftPoll | null> {
  if (USE_MOCK) return mockDraft.removeOption(season, optionId);
  const { poll } = await json<{ poll: DraftPoll | null }>(
    await authedFetch(`${draftBase(season)}/options/${optionId}`, { method: 'DELETE' }),
  );
  return poll;
}

export async function voteDraftPoll(season: number, optionId: string): Promise<DraftPoll | null> {
  if (USE_MOCK) return mockDraft.vote(season, optionId);
  return draftPost(`${draftBase(season)}/vote`, { optionId });
}

export async function lockDraftPoll(season: number, optionId: string): Promise<DraftPoll | null> {
  if (USE_MOCK) return mockDraft.lock(season, optionId);
  return draftPost(`${draftBase(season)}/lock`, { optionId });
}

export async function reopenDraftPoll(season: number): Promise<DraftPoll | null> {
  if (USE_MOCK) return mockDraft.reopen(season);
  return draftPost(`${draftBase(season)}/reopen`);
}

/**
 * In-memory draft poll for the no-backend preview, mirroring the server's rules
 * so the whole two-phase flow (add options, vote, lock, reopen) is exercisable
 * locally. Never reached with a real backend.
 */
const mockDraft = (() => {
  const polls = new Map<number, DraftPoll>();
  const me = 'preview@local';
  const myName = 'Preview';
  const uid = () => globalThis.crypto.randomUUID();
  const seed = (season: number): DraftPoll => ({
    ...emptyDraftPoll(season),
    dateOptions: [
      { id: 'd1', label: 'Sat Aug 30' },
      { id: 'd2', label: 'Sun Aug 31' },
      { id: 'd3', label: 'Sat Sep 6' },
    ],
    dateVotes: {
      'ryan@local': { name: 'Dana Whitfield', options: ['d1', 'd3'] },
      'tj@local': { name: 'Sam Okafor', options: ['d3'] },
      [me]: { name: myName, options: ['d3'] },
    },
  });
  const get = (season: number): DraftPoll | null => polls.get(season) ?? null;
  const ensure = (season: number): DraftPoll => {
    if (!polls.has(season)) polls.set(season, seed(season));
    return polls.get(season)!;
  };
  const save = (poll: DraftPoll) => {
    polls.set(poll.season, poll);
    return poll;
  };
  return {
    async get(season: number) {
      return get(season) ?? ensure(season);
    },
    async create(season: number) {
      return get(season) ?? save(emptyDraftPoll(season));
    },
    reset(season: number) {
      polls.delete(season);
    },
    async addOption(season: number, label: string) {
      const p = ensure(season);
      const option = { id: uid(), label };
      return save(
        p.phase === 'dates'
          ? { ...p, dateOptions: [...p.dateOptions, option] }
          : { ...p, timeOptions: [...p.timeOptions, option] },
      );
    },
    async removeOption(season: number, id: string) {
      const p = ensure(season);
      return save(
        p.phase === 'dates'
          ? { ...p, dateOptions: p.dateOptions.filter((o) => o.id !== id), dateVotes: pruneOption(p.dateVotes, id) }
          : { ...p, timeOptions: p.timeOptions.filter((o) => o.id !== id), timeVotes: pruneOption(p.timeVotes, id) },
      );
    },
    async vote(season: number, optionId: string) {
      const p = ensure(season);
      return save(
        p.phase === 'dates'
          ? { ...p, dateVotes: toggleApproval(p.dateVotes, me, myName, optionId) }
          : { ...p, timeVotes: toggleApproval(p.timeVotes, me, myName, optionId) },
      );
    },
    async lock(season: number, optionId: string) {
      const p = ensure(season);
      if (p.phase === 'dates') {
        const o = p.dateOptions.find((x) => x.id === optionId);
        return save({ ...p, phase: 'times', chosenDate: o?.label ?? '' });
      }
      const o = p.timeOptions.find((x) => x.id === optionId);
      return save({ ...p, phase: 'final', final: { date: p.chosenDate ?? '', time: o?.label ?? '' } });
    },
    async reopen(season: number) {
      const p = ensure(season);
      if (p.phase === 'final') return save({ ...p, phase: 'times', final: null });
      if (p.phase === 'times')
        return save({ ...p, phase: 'dates', chosenDate: null, timeOptions: [], timeVotes: {} });
      return p;
    },
  };
})();
